import {REQUEST_TOKEN_SUCCESS, REFRESH_TOKEN_SUCCESS, DESTROY_TOKEN_SUCCESS} from 'modules/api';
import {logout} from 'modules/auth';

import Grant, {newGrantFromJSON} from 'util/api/Grant';
import ApiError from 'util/api/errors/ApiError';
import NotFoundError from 'util/api/errors/NotFoundError';
import UnauthorizedError from 'util/api/errors/UnauthorizedError';
import BadRequestError from 'util/api/errors/BadRequestError';
import {getNow, getDocument} from 'util/utils';


export const
    HTTP_STATUS_SUCCESS = 200,
    HTTP_STATUS_MULTIPLECHOICES = 300,
    HTTP_STATUS_MOVED_PERMANENTLY = 301,
    HTTP_STATUS_FOUND = 302,
    HTTP_STATUS_BADREQUEST = 400,
    HTTP_STATUS_UNAUTHORIZED = 401,
    HTTP_STATUS_FORBIDDEN = 403,
    HTTP_STATUS_NOTFOUND = 404,
    HTTP_STATUS_TIMEOUT = 408,
    HTTP_STATUS_INTERNALSERVERERROR = 500,
    TOKEN_EXPIRES = 1000;

/**
 * Returns parsed JSON response
 * @param {Object} response The response to parse
 * @returns {Object} The json response
 */
const _parseJSON = response => response.json();


/**
 * Catch connection errors
 * Rethrow exception if not a connection error so it will be catched by whom it may concern
 * @param {TypeError} error The error to handle
 * @returns {undefined} void
 */
const _handleNetError = error => {
    if (error instanceof TypeError) {
        throw new Error('You are disconnected from the Internet or the server is unreachable. Please try again in a few minutes.');
    } else {
        throw error;
    }
};

const _catchRequestToken = error => {
    if (error.response && error.response.status === HTTP_STATUS_UNAUTHORIZED) {
        throw new ApiError('Bad username or password. Please try again.', error.response);
    } else if (error.response && error.response.status === HTTP_STATUS_TIMEOUT) {
        throw new ApiError('The SSO server is unreachable', error.response);
    } else if (error.response && error.response.status === HTTP_STATUS_FORBIDDEN) {
        throw new ApiError("The user doesn't exists. Please contact us", error.response);
    } else {
        throw error;
    }
};

/**
 * Catch when refreshing token fails
 * @param {Error} error The error happened
 */
const _catchRefreshToken = error => {
    if (error.response && error.response.status === HTTP_STATUS_UNAUTHORIZED) {
        throw new ApiError('Sorry, your session has expired. Please sign in again.', error.response);
    } else {
        throw error;
    }
};

export default class Api {
    /**
     * Constructor
     * @param {String} endpoint The base url
     * @param {String} clientId The client id for connecting
     * @param {String} clientSecret The client secret key for connecting
     */
    constructor(endpoint, clientId, clientSecret) {
        this.endpoint = endpoint;
        this.clientId = clientId;
        this.clientSecret = clientSecret;
        this.grant = new Grant();
        this.store = null;
        this.lastTokenRequest = null;

        this.responseInterceptor = this.responseInterceptor.bind(this);
    }

    handleNetError = _handleNetError;
    catchRequestToken = _catchRequestToken;
    parseJSON = _parseJSON;
    //    responseInterceptor = _responseInterceptor;
    catchRefreshToken = _catchRefreshToken;

    /**
     * Bootstraps the class using store data (api key) from localStorage (for example)
     * Leaves a store reference in order to dispatch update events
     * @param {Object} store The redux store
     * @returns {undefined} void
     */
    syncReduxAndApi(store) {
        const
            currentState = store.getState(),
            grantSession = window.sessionStorage.getItem('grant');

        this.store = store;

        if (grantSession) {
            this.grant = new Grant(JSON.parse(grantSession));
        } else {
            window.sessionStorage.setItem('grant', JSON.stringify(currentState.api));
            this.grant = new Grant(currentState.api);
        }
    }

    /**
     * Dispatch action to the store if set
     * @param {Object} action The action to dispatch
     * @returns {undefined} void
     */
    notify(action) {
        if (this.store) {
            this.store.dispatch(action);
        }
    }

    /*// TODO PRUEBA DE ACCESS TOKEN
    export const getAccessToken(){
        return this.grant.accessToken;
    }*/

    /**
     * Returns if token is valid according to expiration date
     * @returns {boolean} if the token is valid
     */
    isTokenValid() {
        const
            expiryDate = this.grant.lastUpdate + this.grant.accessTokenExpiresIn * TOKEN_EXPIRES,
            now = getNow(),
            isExpired = expiryDate < now,
            ret = !!this.grant.accessToken && !isExpired;

        console.info('Token expires', new Date(expiryDate).toLocaleString(), 'is valid:', ret);

        return ret;
    }

    saveToken(json) {
        this.grant = newGrantFromJSON(json);
        sessionStorage.setItem('grant', JSON.stringify(this.grant));
        this.notify({type: REQUEST_TOKEN_SUCCESS, grant: this.grant});
        this.lastTokenRequest = null;

        return json;
    }

    /**
     * Requests initial grant using credentials
     * @param {String} user The user to do login
     * @param {String} pass The password to do login
     * @returns {Promise} Promise of the request
     */
    requestAccessToken(user, pass) {
        const data = new FormData();

        data.append('grant_type', 'password');
        data.append('username', user);
        data.append('password', pass);
        data.append('client_id', this.clientId);
        data.append('client_secret', this.clientSecret);

        this.lastTokenRequest = fetch(`${this.endpoint}/o/token/`, {
            method:  'post',
            body:    data,
            headers: {
                accept: 'application/json'
            }
        })
            .then(this.responseInterceptor)
            .then(this.parseJSON)
            .then(this.saveToken.bind(this))
            .catch(this.catchRequestToken)
            .catch(this.handleNetError);

        return this.lastTokenRequest;
    }


    /**
     * Requests initial grant using credentials
     * @param {String} code The sso code
     * @returns {Promise} Promise of the request
     */
    requestSsoAccessToken(code) {
        this.lastTokenRequest = fetch(`${this.endpoint}/sso/customer?code=${code}`, {
            method:  'get',
            headers: {
                accept: 'application/json'
            }
        })
            .then(this.responseInterceptor)
            .then(this.parseJSON)
            .then(this.saveToken.bind(this))
            .catch(this.catchRequestToken)
            .catch(this.handleNetError);

        return this.lastTokenRequest;
    }

    /**
     * Requests initial grant using credentials
     * @param {String} code The sso code
     * @returns {Promise} Promise of the request
     */
    requestKeycloackAccessToken(code) {
        this.lastTokenRequest = fetch(`${this.endpoint}/sso/keycloack?code=${code}`, {
            method:  'get',
            headers: {
                accept: 'application/json'
            }
        })
            .then(this.responseInterceptor)
            .then(this.parseJSON)
            .then(this.saveToken.bind(this))
            .catch(this.catchRequestToken)
            .catch(this.handleNetError);

        return this.lastTokenRequest;
    }

    /**
     * Requests initial grant using credentials
     * @param {String} code The sso code
     * @returns {Promise} Promise of the request
     */
    requestCustomerAccessToken(code) {
        this.lastTokenRequest = fetch(`${this.endpoint}/sso/customer?code=${code}`, {
            method:  'get',
            headers: {
                accept: 'application/json'
            }
        })
            .then(this.responseInterceptor)
            .then(this.parseJSON)
            .then(this.saveToken.bind(this))
            .catch(this.catchRequestToken)
            .catch(this.handleNetError);

        return this.lastTokenRequest;
    }

    refreshToken(json) {
        this.grant = newGrantFromJSON(json);
        sessionStorage.setItem('grant', JSON.stringify(this.grant));
        this.notify({type: REFRESH_TOKEN_SUCCESS, grant: this.grant});
        this.lastTokenRequest = null;

        return json;
    }

    /**
     * Requests further grants using refresh token
     * @returns {Promise} Promise of the request
     */
    refreshAccessToken() {
        const data = new FormData();

        data.append('grant_type', 'refresh_token');
        data.append('refresh_token', this.grant.refreshToken);
        data.append('client_id', this.clientId);
        data.append('client_secret', this.clientSecret);

        this.lastTokenRequest = fetch(`${this.endpoint}/o/token/`,
            {
                method:  'post',
                body:    data,
                headers: {
                    accept: 'application/json'
                }
            })
            .then(this.responseInterceptor)
            .then(this.parseJSON)
            .then(this.refreshToken.bind(this))
            .catch(this.catchRefreshToken)
            .catch(this.handleNetError);

        return this.lastTokenRequest;
    }

    /**
     * Removes the stored token
     * @returns {undefined} void
     */
    destroyAccessToken() {
        this.grant = newGrantFromJSON();
        sessionStorage.setItem('grant', JSON.stringify(this.grant));
        this.notify({type: DESTROY_TOKEN_SUCCESS, grant: this.grant});
    }

    composeEndpoint(url) {
        return url.startsWith('http') ?
            url :
            `${this.endpoint}/${url}`;
    }

    composeArgs(method, payload) {

        const ret = {
            method,
            body:    payload instanceof FormData ? payload : typeof payload === 'object' ? JSON.stringify(payload) : undefined,
            headers: {
                accept:          'application/json',
                'cache-control': 'no-cache',
                authorization:   this.grant.accessToken ? `Bearer ${this.grant.accessToken}` : undefined
            }
        };

        if (!(payload instanceof FormData) && typeof payload === 'object') {
            ret.headers['Content-Type'] = 'application/json';
        }

        return ret;
    }

    doRefreshToken(method, url, params, payload) {
        const _getPromise = () => this.lastTokenRequest || this.refreshAccessToken();


        return _getPromise()
            .then(() => {
                console.warn(url, 'New token received, retrying original request');

                return this.request(method, url, params, payload);
            })
            .catch(error => {

                this.notify(logout());
            });
    }

    getCatchRequest(method, url, params, payload) {
        return error => {
            if (error.response && error.response.status === HTTP_STATUS_FORBIDDEN) {
                return this.doRefreshToken(method, url, params, payload);
            }
            throw error;
        };
    }

    download(url, defaultFileName) {
        const
            endpoint = this.composeEndpoint(url),
            {headers} = this.composeArgs('get');

        return fetch(endpoint, {
            method:  'GET',
            headers: {
                authorization: headers.authorization
            }
        })
            .then(res => Api.resolveDownload(res, defaultFileName))
            .catch(this.handleNetError);
    }
    static getDownloadFileName(headers, defaultFileName) {
        const TOKEN = '%date%';

        if (headers && headers.has('X-download-filename')) {
            /*Until we figure out how to read properly the headers with fetch*/
            return headers.get('X-download-filename');
        } else if (~defaultFileName.indexOf(TOKEN)) {
            const currentDate = new Date(getNow());
            const textDate = `${currentDate.getUTCFullYear()}-${currentDate.getUTCMonth()}-${currentDate.getUTCDate()}`;


            return defaultFileName.replace(TOKEN, textDate);
        }

        return defaultFileName;
    }
    static resolveDownload(res, defaultFileName = 'download.csv') {
        const anchor = getDocument().createElement('a'); // Create a <button> element

        getDocument().body.appendChild(anchor);

        const fileName = Api.getDownloadFileName(res.headers, defaultFileName);

        return res.arrayBuffer().then(body => {
            Api.finishDownload(body, anchor, fileName);
        });
    }
    static finishDownload(body, anchor, fileName) {
        const windowUrl = window.URL || window.webkitURL;
        const urlData = windowUrl.createObjectURL(new Blob([body], {type: 'application/csv'}));

        anchor.setAttribute('href', urlData);
        anchor.setAttribute('download', fileName);
        anchor.click();
        windowUrl.revokeObjectURL(urlData);
    }
    request(method, url, params = {}, payload, noAuth = false) {
        const
            endpoint = this.composeEndpoint(url),
            args = this.composeArgs(method, payload);

        if (!noAuth) {
            const {accessTokenExpiresIn, lastUpdate} = this.store.getState().api;
            const now = new Date(getNow());
            const date = new Date(lastUpdate);

            date.setSeconds(date.getSeconds() + accessTokenExpiresIn);

            if (now > date) {
                return this.doRefreshToken(method, url, params, payload);
            }
        }

        return fetch(endpoint, args)
            .then(this.responseInterceptor)
            //.catch(this.getCatchRequest(method, url, params, payload))
            .catch(this.handleNetError);
    }

    requestParsing(method, url, data, noAuth) {
        return this.request(method, url, undefined, data, noAuth)
            .then(this.parseJSON);
    }

    /**
     * Perform a GET request
     * @param {String} url The url to get
     * @returns {Promise} Promise of the request
     */
    get(url) {
        return this.requestParsing('get', url);
    }

    post(url, data, noAuth) {
        return this.requestParsing('post', url, data, noAuth);
    }

    postFile(url, files, params, noAuth) {
        const newData = new FormData();

        if (files instanceof Array) {
            files.forEach(file => {
                newData.append(file.name, file, file.name);
            });
        } else {
            newData.append(files.name, files, files.name);
        }

        if (params) {
            Object.keys(params).forEach(key => {
                newData.append(key, params[key]);
            });
        }

        return this.post(url, newData, noAuth);
    }

    /**
     * Perform a PUT request
     * @param {String} url The url to put
     * @param {Object} data The data to send
     * @returns {Promise} Promise of the request
     */
    put(url, data) {
        return this.requestParsing('put', url, data);
    }

    /**
     * Perform a DELETE request
     * @param {String} url The url to delete
     * @returns {Promise} Promise of the request
     */
    delete_(url) {
        return this.request('delete', url);
    }
    /**
     * Handle 'fetch' responses
     * If HTTP error, throws exceptions to reject promises (like jQuery)
     * Note: it won't execute if 'fetch' couln't even connect
     * @param {Object} response The response to intercept
     * @returns {undefined} void
     */
    responseInterceptor(response) {
        if (response.status >= HTTP_STATUS_SUCCESS && response.status < HTTP_STATUS_MULTIPLECHOICES) {
            return response;
        }

        switch (response.status) {
        case HTTP_STATUS_NOTFOUND:
            throw new NotFoundError(response);
        case HTTP_STATUS_BADREQUEST:
            throw new BadRequestError(response);
        case HTTP_STATUS_INTERNALSERVERERROR:
            throw new ApiError('Sorry, there was a server error. Please try again.', response, 'INTERNAL_SERVER_ERROR');
        case HTTP_STATUS_UNAUTHORIZED:
            throw new UnauthorizedError(response);
        case HTTP_STATUS_MOVED_PERMANENTLY:
        case HTTP_STATUS_FOUND:
            this.notify(logout());
            throw new ApiError('Sorry, it seems the login was lost. Try to login again', response, 'UNAUTHORIZED');
        default:
            throw new ApiError(response.statusText, response);
        }
    }
}



// WEBPACK FOOTER //
// ./src/util/api/Api.js